<template>
    <div class="media-wrapper" :class="wrapperClasses" :style="mediaStyle">
        <template v-if="computedType === 'screenshot' || computedType === 'image'">
            <div
                v-if="computedType === 'screenshot'"
                :class="{ 'no-shadow': !isShadow }"
                class="media screenshot-wrapper"
            >
                <div
                    v-if="enlargedImage"
                    ref="enlargedTriggerRef"
                    v-modal="computedModalId"
                    tabindex="0"
                    role="button"
                    :aria-label="computedEnlargeLabel"
                    @click="onClickMedia"
                    @keydown.enter="onClickMedia"
                >
                    <div class="enlarged-image screenshot-image">
                        <a-picture
                            v-bind="$props"
                            :alt="resolvedAlt"
                            :class="className"
                            class="full-width"
                            :is-shadow="false"
                        />
                    </div>
                    <div v-if="enlargedImage" class="zoom-in">
                        <a-glyph name="zoom-in" class="icon" :aria-hidden="true" />
                    </div>
                </div>

                <template v-if="!disableModal">
                    <a-modal
                        v-if="enlargedImage"
                        :id="computedModalId"
                        :is-overlay-close="isOverlayClose"
                        :caption="caption"
                        :type="isGallery ? 'gallery' : 'screenshot'"
                    >
                        <template #default>
                            <a-picture
                                :link="enlargedImage"
                                :alt="resolvedAlt"
                                position="center"
                                :is-shadow="false"
                                persist-original
                            />
                        </template>
                        <template v-if="isGallery" #gallery>
                            <a-modal-gallery
                                :caption="caption"
                                :next-id="nextId"
                                :prev-id="prevId"
                            />
                        </template>
                    </a-modal>

                    <a-picture
                        v-else
                        v-bind="$props"
                        :alt="resolvedAlt"
                        :class="className"
                        :is-shadow="false"
                        class="full-width screenshot-image"
                    />
                </template>
            </div>

            <a-picture
                v-else-if="computedType === 'image'"
                v-bind="$props"
                :alt="resolvedAlt"
                :class="className"
                :is-shadow="false"
                class="media full-width"
            />

            <template v-if="ctaSettings">
                <template v-if="hasInteractionDemo(ctaSettings)">
                    <div ref="ctaBlockRef" class="cta-block">
                        <a-button
                            ref="ctaButtonRef"
                            v-modal="`${ctaSettings.modal.id}-media`"
                            tabindex="0"
                            class="cta-button"
                            v-bind="ctaSettings"
                            @click="logInteraction(ctaSettings)"
                        />
                    </div>
                    <a-demo-modal
                        v-if="isVisibleDemoModal"
                        :modal="{...ctaSettings.modal, id: `${ctaSettings.modal.id}-media`}"
                    />
                </template>

                <div
                    v-else
                    ref="ctaBlockRef"
                    class="cta-block"
                    @click="onCtaBlockClick"
                >
                    <a-button
                        ref="ctaButtonRef"
                        tabindex="0"
                        class="cta-button"
                        v-bind="ctaSettings"
                        @click="onCtaButtonClick"
                    />
                </div>
            </template>
        </template>

        <a-video-card
            v-else-if="computedType === 'video'"
            v-bind="$props"
            :image-alt="resolvedImageAlt"
            :cta="videoCtaSettings"
            :class="className"
            class="media"
            @progressChanged="$emit('progressChanged', $event)"
        />

        <span v-else>ERR: Unsupported media type!</span>

        <template v-if="badge && (computedType === 'screenshot' || computedType === 'video')">
            <div
                class="badge"
                :style="{
                    top: `${badge?.offsetTop || -12}px`,
                    insetInlineEnd: `${badge?.offsetRight || 48}px`
                }"
            >
                <dynamic-link
                    v-if="badge?.to"
                    :to="badge.to"
                    :target="badge.target || '_blank'"
                >
                    <a-picture :link="badge.image" :alt="badge.alt || 'Badge'" />
                </dynamic-link>
                <a-picture
                    v-else
                    :link="badge"
                    alt="Badge"
                />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import AButton from '@core/components/button/button.vue';
import ADemoModal from '@core/components/demo-modal/demo-modal.vue';
import DynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import AModalGallery from '@core/components/modal/modal-gallery.vue';
import AModal from '@core/components/modal/modal.vue';
import APicture from '@core/components/picture/picture.vue';
import AVideoCard from '@core/components/video-card/video-card.vue';

import { useBreakpoint } from '@core/composables/useBreakpoint';
import { useInteractionDemo } from '@core/composables/useInteractionDemo';

import Modal from '@core/directives/modal.js';
import { getElementPath } from '@utils/analytics';
import { TYPES, BORDER_RADIUS } from './constants.js';

defineOptions({
    name: 'AMedia',
    directives: { Modal },
});

type BadgeObject = {
    image?: string;
    alt?: string;
    to?: any;
    target?: string;
    offsetTop?: number;
    offsetRight?: number;
};

type Badge = string | BadgeObject;

type CtaModal = {
    id?: string;
    src?: string;
    breakpoint?: string[];
    abbr?: string;
};

type CtaSettings = {
    text?: string;
    type?: string;
    size?: string;
    glyph?: string;
    to?: any;
    modal?: CtaModal;
    [key: string]: any;
};

type DomRef = Element | { $el?: Element } | null | undefined;

const props = defineProps({
    type: {
        type: String,
        validator: (type: string) => TYPES.includes(type),
        default: 'image',
    },
    link: { type: String, default: '' },
    alt: { type: String, default: undefined },
    fit: { type: [String, Object], default: undefined },
    position: { type: String, default: 'center' },
    enlargedImage: { type: String, default: null },
    persistOriginal: { type: Boolean, default: false },
    disableModal: { type: Boolean, default: false },
    modalId: { type: String, default: null },
    caption: { type: String, default: null },
    nextId: { type: String, default: null },
    prevId: { type: String, default: null },
    isOverlayClose: { type: Boolean, default: true },
    isShadow: { type: Boolean, default: true },
    hideEffects: { type: Boolean, default: false },
    isBackground: { type: Boolean, default: false },
    background: { type: Object, default: undefined },
    size: { type: String, default: undefined },
    ytId: { type: String, default: undefined },
    autoplayOnOpen: { type: Boolean, default: true },
    text: { type: String, default: undefined },
    time: { type: String, default: undefined },
    image: { type: String, default: undefined },
    badge: { type: [String, Object] as unknown as () => Badge, default: undefined },
    imageAlt: { type: String, default: undefined },
    isEager: { type: Boolean, default: false },
    cta: { type: Object as unknown as () => CtaSettings | undefined, default: undefined },
    events: { type: Object as unknown as () => any, default: undefined },
    borderRadius: {
        type: Number,
        default: 4,
        validator: (value) => !value || BORDER_RADIUS.includes(value),
    },
    borderRadiusWrapper: {
        type: Number,
        default: undefined,
        validator: (value) => !value || BORDER_RADIUS.includes(value),
    },
});

const emit = defineEmits<{
    (e: 'click', event: Event): void;
    (e: 'progressChanged', payload: any): void;
}>();

const enlargedTriggerRef = ref<HTMLElement | null>(null);
const ctaBlockRef = ref<HTMLElement | null>(null);
const ctaButtonRef = ref<any>(null); // AButton instance

const { currentBreakpoint } = useBreakpoint();
const { isVisibleDemoModal, hasInteractionDemo, logInteraction } = useInteractionDemo(currentBreakpoint);

const computedType = computed(() => (props.type ? props.type : 'image'));

// `alt` describes pictures, `imageAlt` describes the video poster — slices usually supply only one of them
const resolvedAlt = computed(() => props.alt || props.imageAlt);
const resolvedImageAlt = computed(() => props.imageAlt || props.alt);

const ctaSettings = computed<CtaSettings | null>(() => {
    if (!props.cta || props.enlargedImage) return null;
    if (!['screenshot', 'image'].includes(computedType.value)) return null;

    const fallback: CtaSettings = {
        text: 'Button',
        type: 'main',
        size: 'small',
        glyph: 'arrow',
    };

    return { ...fallback, ...props.cta };
});

const videoCtaSettings = computed<CtaSettings | null>(() => {
    if (!props.cta) return null;
    if (computedType.value !== 'video') return null;

    const fallback: CtaSettings = {
        text: 'Play video',
        type: 'main',
        size: 'small',
        glyph: 'i-play-round--16',
    };

    return { ...fallback, ...props.cta };
});

const wrapperClasses = computed(() => ({
    [`media-wrapper--${computedType.value}`]: true,
    'media-with-cta': Boolean(ctaSettings.value || videoCtaSettings.value),
    'media-with-enlarge': Boolean(props.enlargedImage),
    'has-outline': Boolean(props.enlargedImage) || Boolean(ctaSettings.value || videoCtaSettings.value),
    'hide-effects': props.hideEffects,
    'has-border-wrapper': props.borderRadiusWrapper != null,
}));

const className = computed(() => ({
    [`a-media__type__${props.type}`]: props.type,
}));

const mediaStyle = computed(() => ({
    '--a-media-border-radius': `${props.borderRadius}px`,
    ...(props.borderRadiusWrapper != null && {
        '--a-media-border-radius-wrapper': `${props.borderRadiusWrapper}px`,
    }),
}));

const computedModalId = computed(() => (props.modalId ? props.modalId : `${props.link}-${props.enlargedImage}`));

const isGallery = computed(() => Boolean(props.caption || (props.prevId && props.nextId)));

const computedEnlargeLabel = computed(() => {
    if (props.caption) return props.caption;
    if (resolvedAlt.value) return resolvedAlt.value;
    return 'View larger image';
});

function eventsGA(event: any) {
    if (props.events) return props.events;
    return {
        open: eventGA(event, 'expand'),
        close: eventGA(event, 'close'),
    };
}

function eventGA(event: any, status: string) {
    return {
        event: 'Acronis',
        eventCategory: 'Interactions',
        eventAction: 'Screenshots',
        eventLabel: props.link.replace('@', ''),
        eventLocation: getElementPath(event.target),
        eventContext: status,
        eventButton: event.target?.dataset?.id || '0',
    };
}

function onClickMedia(event: any) {
    const modalEvents = eventsGA(event);
    localStorage.setItem('modalEvents', JSON.stringify(modalEvents));
}


function toEl(target: DomRef): Element | null {
    if (!target) return null;
    if (target instanceof Element) return target;
    if (typeof target === 'object' && target.$el instanceof Element) return target.$el;
    return null;
}

function tagEnlargedImagesWithIndex() {
    const root = toEl(enlargedTriggerRef.value);
    if (!root) return;

    root.querySelectorAll<HTMLElement>('.a-picture__img').forEach((img, index) => {
        img.setAttribute('data-id', String(index + 1));
    });
}

function setCtaButtonState(state: 'hover' | 'active', enabled: boolean) {
    const btn = toEl(ctaButtonRef.value);
    if (!btn) return;

    btn.classList.toggle(state, enabled);
}

function addCtaListeners() {
    if (!ctaSettings.value) return;

    const block = toEl(ctaBlockRef.value);
    if (!block) return;

    block.addEventListener('mouseenter', onCtaMouseEnter);
    block.addEventListener('mouseleave', onCtaMouseLeave);
    block.addEventListener('mousedown', onCtaMouseDown);
    block.addEventListener('mouseup', onCtaMouseUp);
}

function removeCtaListeners() {
    const block = toEl(ctaBlockRef.value);
    if (!block) return;

    block.removeEventListener('mouseenter', onCtaMouseEnter);
    block.removeEventListener('mouseleave', onCtaMouseLeave);
    block.removeEventListener('mousedown', onCtaMouseDown);
    block.removeEventListener('mouseup', onCtaMouseUp);
}

function onCtaMouseEnter() {
    setCtaButtonState('hover', true);
}
function onCtaMouseLeave() {
    setCtaButtonState('hover', false);
}
function onCtaMouseDown() {
    setCtaButtonState('active', true);
}
function onCtaMouseUp() {
    setCtaButtonState('active', false);
}

function onCtaBlockClick(event: Event) {
    if (props.cta?.to) {
        const btn = toEl(ctaButtonRef.value) as HTMLElement | null;
        btn?.click();
        return;
    }

    emit('click', event);
}

function onCtaButtonClick(event: Event) {
    event.stopPropagation();
}

onMounted(() => {
    tagEnlargedImagesWithIndex();
    addCtaListeners();
});

onBeforeUnmount(() => {
    removeCtaListeners();
});
</script>

<style lang="pcss" scoped>
.full-width :deep(.a-picture__img) {
    width: 100%;
}

.enlarged-image {
    position: relative;
    cursor: pointer;
    border-radius: var(--a-media-border-radius);
    &:hover {
        .a-media__type__screenshot {
            box-shadow: none;
        }
    }
}

.badge {
    position: absolute;
    top: -8px;
    inset-inline-end: 32px;
    width: auto !important;
    height: auto !important;
    filter: drop-shadow(0px 4px 8px rgba(36, 49, 67, .3));
    @media (--viewport-desktop) {
        top: -12px;
        inset-inline-end: 48px;
    }
    &:deep(.a-picture__img) {
        max-height: 64px;
        max-width: 64px;
        @media (--viewport-desktop) {
            max-height: 72px;
            max-width: 72px;
        }
    }
}

.zoom-in {
    display: flex;
    position: absolute;
    bottom: 8px;
    inset-inline-end: 8px;
    background: var(--av-fixed-lighter);
    border-radius: var(--a-media-border-radius);
    padding: 8px;
    transition: 0.2s all;

    .icon {
        fill: var(--av-inversed-primary);
    }
}

.a-media {
    &_radius {
        &_0 { --a-media-border-radius: 0px }
        &_2 { --a-media-border-radius: 2px }
        &_4 { --a-media-border-radius: 4px; }
        &_8 { --a-media-border-radius: 8px; }
        &_12 { --a-media-border-radius: 12px; }
        &_16 { --a-media-border-radius: 16px; }
        &_20 { --a-media-border-radius: 20px; }
        &_24 { --a-media-border-radius: 24px; }
        &_28 { --a-media-border-radius: 28px; }
    }
    &__type {
        &__screenshot {
            border-radius: var(--a-media-border-radius);
            overflow: hidden;
            transition: box-shadow 0.2s ease-in-out;
        }
    }
}
.a-media__type__video {
    border-radius: var(--a-media-border-radius);
    :deep(.a-video-card__wrapper) {
        border-radius: var(--a-media-border-radius);
        overflow: hidden;
    }
}
.a-media__type__video :deep(.a-video-card__wrapper:before) {
    content: none;
}

.no-shadow {
    .a-media__type__screenshot {
        box-shadow: none;
    }
}

.cta-block {
    position: absolute;
    inset: 0;
    display: flex;
    user-select: none;
    align-items: center;
    justify-content: center;
}

.cta-block :deep(.a-button__content) {
    user-select: none;
}

.cta-button {
    transition: box-shadow ease-in-out 0.2s;
}

.media-wrapper {
    user-select: none;
    position: relative;

    &.has-border-wrapper {
        background: var(--av-inversed-invisible);
        padding: var(--a-media-border-radius-wrapper);
        border-radius: var(--a-media-border-radius-wrapper);
    }

    .a-picture {
        width: 100%;
        height: 100%;
    }

    &.has-outline {
        &:focus-within,
        &:focus-visible {
            .media {
                outline: 3px solid var(--av-brand-secondary);
                outline-offset: -1px;
                border-radius: var(--a-media-border-radius);
            }
        }
    }

    &--image.media-with-cta {
        .a-picture {
            transition: opacity 0.2s ease-in-out;
            will-change: opacity;
        }

        &:hover {
            cursor: pointer;

            .a-picture {
                opacity: .6;
            }
        }
    }

    &--screenshot.media-with-cta {
        .screenshot-wrapper {
            overflow: hidden;
            border-radius: var(--a-media-border-radius);
            border: 1px solid var(--av-brand-secondary-light, rgba(64, 139, 234, 0.3));
            box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
            transition: opacity .2s ease-in-out, box-shadow .2s ease-in-out;
            will-change: opacity;
        }

        .a-picture {
            transition: transform 0.2s ease-in-out;
            will-change: transform;
        }

        .cta-block {
            background: radial-gradient(50.09% 77.73% at 50.09% 50%, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0) 100%);
        }

        &:hover {
            cursor: pointer;

            .screenshot-wrapper {
                box-shadow: none;
                opacity: 0.6;
                .a-picture {
                    transform: scale(1.02);
                }
            }

        }
    }

    &--screenshot.media-with-enlarge {
        .screenshot-image {
            overflow: hidden;
            border-radius: var(--a-media-border-radius);
            border: 1px solid var(--av-brand-secondary-light, rgba(64, 139, 234, 0.3));
            box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
            transition: opacity 0.2s ease-in-out;
            will-change: opacity;
        }

        .a-picture {
            transition: transform 0.2s ease-in-out;
            will-change: transform;
        }

        .zoom-in {
            background: var(--av-fixed-lighter);
            &:hover {
                background: var(--av-button-main-hover);
                box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
            }
            &:active {
                background: var(--av-button-main-active);
                box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
            }
        }

        &:hover {
            cursor: pointer;

            .screenshot-image {
                box-shadow: none;
                opacity: 0.6;
            }

            .a-picture {
                transform: scale(1.02);
            }
        }
    }

    &--screenshot:not(.media-with-enlarge, .media-with-cta) {
        .screenshot-image {
            overflow: hidden;
            border-radius: var(--a-media-border-radius);
            border: 1px solid var(--av-brand-secondary-light, rgba(64, 139, 234, 0.3));
            box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
            transition: opacity 0.2s ease-in-out;
            will-change: opacity;
        }
    }

    &.hide-effects {
        &.media-wrapper--image.media-with-cta:hover {
            .a-picture {
                opacity: 1;
            }
        }

        &.media-wrapper--screenshot.media-with-cta {
            .cta-block {
                background: none;
            }

            &:hover {
                .screenshot-wrapper {
                    opacity: 1;
                }
            }
        }

        &.media-wrapper--video {
            :deep(.a-video-card__img) {
                &::before,
                &::after {
                    display: none;
                }
            }
        }
    }

    &:hover {
        .zoom-in {
            fill: var(--av-button-main);
            background: var(--av-button-main);
            box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
        }

        .cta-button {
            box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
        }
    }
    &:active {
        .zoom-in {
            background: var(--av-button-main-active);
            box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
        }
        .a-button {
            box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
        }
    }
}
</style>
